import { apiClient } from "@/shared/api/client";
import { apiRoutes } from "@/shared/api/routes";

export interface SendFeedbackPayload {
  comment: string;
  attachments?: File[];
  url?: string;
  details?: string;
  category?: string;
  errorMessages?: string[];
}

export interface SendFeedbackResponse {
  errors: Array<{ message: string }>;
  data: { message?: string };
  warnings: string[];
  info: string[];
}

export const createSendFeedbackService = () => ({
  submitFeedback: async (payload: SendFeedbackPayload): Promise<SendFeedbackResponse> => {
    const { comment, attachments = [], url, details, category = "general", errorMessages = [] } = payload;

    // Only keep real File instances (filter out nulls / empty slots from the modal)
    const validFiles = attachments.filter((f): f is File => f instanceof File && f.size > 0);

    let response;

    if (validFiles.length > 0) {
      // Multipart form – omit Content-Type so Axios sets the correct multipart boundary
      const form = new FormData();
      form.append("comment", comment);
      if (url) form.append("url", url);
      if (details) form.append("details", details);
      form.append("category", category);
      errorMessages.forEach((msg) => form.append("error_messages[]", msg));
      validFiles.slice(0, 3).forEach((file) => form.append("attachments[]", file));

      response = await apiClient.post<SendFeedbackResponse>(
        apiRoutes.feedback(),
        form,
      );
    } else {
      // JSON body – no attachments field
      response = await apiClient.post<SendFeedbackResponse>(
        apiRoutes.feedback(),
        {
          comment,
          ...(url ? { url } : {}),
          ...(details ? { details } : {}),
          category,
          ...(errorMessages.length > 0 ? { error_messages: errorMessages } : {}),
        },
      );
    }

    const json = response.data;

    // Surface application-level errors returned with a 200 status
    if (json?.errors?.length > 0) {
      throw new Error(json.errors[0].message);
    }

    return json;
  },
});

export type SendFeedbackService = ReturnType<typeof createSendFeedbackService>;